import { Footer, Group } from './styles';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { addReactionOnMessage } from '../../../services/projectServices';
import { useLocation, useParams } from 'react-router-dom';
import { ReplyMessageDataInterface } from '../../../interfaces/MessageInterface';
import { updateDiscussionMessageDetail } from '../../../services/chatMessageService';
import { syncConversation } from '../../../services/chatMessageSyncService';
import { CHAT_GROUP_TYPE } from '../../../global/constants';
import { captureException } from '../../../services/logService';
import { nanoid } from 'nanoid';
import {
  ChatGroupInterface,
  MessageDetailsInterface,
  MessageReplyInterface
} from '../../../interfaces/chatMessageInterface';
import { useMobileDevice } from '../../../global/useMobile';
import MessageInputSection from './messageInputSection';
import { MESSAGE_ANALYTICS } from '../../../global/analyticsConstants';
import MessageModals from './messageModals';

import { useDraftMessage } from './hooks/useDraftMessage';
import { useEmojiState } from './hooks/useEmojiState';
import { useMessageEditing } from './hooks/useMessageEditing';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { removeFileFromFirebase } from '../../../helpers/common';
import { TiptapEditorRef } from '../../../interfaces/tiptapInterface';
import { rootStore } from '../../../mobx/rootStore';
import { RawProjectFile } from '../../../mobx/interfaces/project';
import RenderExistingFileV2 from '../../renderExistingFile';
import { UserModel } from '../../../mobx/models/user';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import SubNavigation from '../../bottomNavigation/subNavigation';
import { ProjectModel } from '../../../mobx/models/project';
import { useFigmaRefresh } from './hooks/useFigmaRefresh';
import { useSendMessage } from './hooks/useSendMessage';
import useFileSelection from './hooks/useFileSelection';
import {
  MessageFeedProvider,
  MessageFeedEnvironment,
  FileSelectionState,
  MessageActionsContextValue,
  MessageSelectionContextValue,
  MessageEnvironmentContextValue
} from './MessageFeedContext';
import MessageFileDrawer from './messageFileDrawer';
import MessageList from './messageList';
dayjs.locale('en');

interface Props {
  loading: boolean;
  messages: MessageDetailsInterface[];
  loadData: () => void;
  projectAllFiles: RawProjectFile[];
  currentProject: ProjectModel | ChatGroupInterface | null;
  isMessageLoading?: boolean;
  rightElement?: React.ReactNode;
  /** Environment + file selection config passed via context */
  environment: MessageFeedEnvironment;
  fileSelection: FileSelectionState;
  newGroup?: boolean;
}

const MessagesFeed = observer(
  ({
    loading,
    messages,
    projectAllFiles,
    currentProject,
    isMessageLoading,
    rightElement,
    environment,
    fileSelection,
    newGroup
  }: Props) => {
    const { isNavbarMessage, isProjectMessageDetail, isProjectDetail } = environment;

    //Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const inlineRef = useRef<TiptapEditorRef>(null);
    const emojiButtonRef = useRef<React.RefObject<HTMLDivElement>[]>([]);
    const subNavigationRef = useRef<HTMLDivElement>(null);
    const iconSectionRef = useRef<HTMLDivElement>(null);

    // States
    const [deletedMessageId, setDeletedMessageId] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [replyMessage, setReplyMessage] = useState<ReplyMessageDataInterface>({});
    const [displayedMessages, setDisplayedMessages] = useState<MessageDetailsInterface[]>([]);
    const [currentIndex, setCurrentIndex] = useState(messages?.length - 1);
    const [isSnapModalOpen, setIsSnapModalOpen] = useState(false);
    const [openGifModal, setIsOpenGifModal] = useState(false);
    const [isOpenUserModal, setIsOpenUserModal] = useState(false);
    const [isCommentModal, setIsCommentModal] = useState<boolean>(false);
    const [subNavigationHeight, setSubNavigationHeight] = useState<number>(0);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    //mobx store variables
    const { appStore, currentUser, workspaceStore, richTextStore } = rootStore;
    const { isBubbleMenuOpen } = appStore;
    const { currentWorkspace: workspace } = workspaceStore;
    const { content, uploadedFiles } = richTextStore;

    // Other variables
    const params: { id: string } = useParams();
    const mobile = useMobileDevice();
    const newGroupState = !!(isNavbarMessage && newGroup);
    const userUseEmoji = UserPreferenceSingleton.getInstance().getUserUseEmoji() as string[] | null;
    const { search } = useLocation();
    const {
      openEmoji,
      selectedMesaage,
      emojiLoading,
      setEmojiLoading,
      setOpenEmoji,
      setSelectedMessage,
      closeEmojiDropdown,
      handleOpenChange
    } = useEmojiState();
    const { editingMessage, isOpenEditMessageModal, setEditingMessage, Cancel, editMessageData } = useMessageEditing({
      setReplyMessage,
      setSelectedMessage,
      store: rootStore
    });
    const chatGroup = useMemo(() => {
      return new URLSearchParams(search).get('chatGroup');
    }, [search]);

    const { attachment, selectFile, setAttachment, setSelectFile, onClickFile, onSelectFile } = useFileSelection({
      currentProject,
      isNavbarMessage,
      attachment: fileSelection.attachment,
      selectFile: fileSelection.selectFile,
      setAttachment: fileSelection.setAttachment,
      setSelectFile: fileSelection.setSelectFile,
      inlineRef
    });

    const fileSelectionState: FileSelectionState = {
      attachment,
      selectFile,
      setAttachment,
      setSelectFile
    };

    const setDraftContent = useCallback((value: string) => {
      rootStore.richTextStore.setContent(value);
    }, []);

    const setDraftUploadedFiles = useCallback((files: CommentFilesInterface[]) => {
      rootStore.richTextStore.setUploadedFiles(files);
    }, []);

    const { clearDraft } = useDraftMessage({
      content,
      uploadedFiles,
      currentProjectId: currentProject?.id,
      isNavbarMessage,
      routeChatGroupId: params?.id,
      inlineRef,
      setContent: setDraftContent,
      setUploadedFiles: setDraftUploadedFiles,
      isEditing: isOpenEditMessageModal
    });

    useEffect(() => {
      if (inlineRef?.current) {
        inlineRef?.current.clearContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }
    }, [currentProject?.id, inlineRef]);

    useEffect(() => {
      const handleFocus = (event: FocusEvent) => {
        const target = event.target as HTMLElement;
        const isInputField = target.tagName === 'INPUT';
        const isTextArea = target.tagName === 'TEXTAREA';
        const isTipTapEditor = target.className === 'tiptap ProseMirror';

        if (isInputField || isTextArea || isTipTapEditor) {
          setIsKeyboardVisible(true);
        }
      };
      const handleBlur = () => {
        setTimeout(() => {
          if (!iconSectionRef.current) return;
          const activeElement = document.activeElement;
          if (iconSectionRef.current?.contains(activeElement)) {
            return;
          }

          if (document.querySelector('[data-improve-modal]')) {
            return;
          }

          // The emoji picker is rendered in a portal outside iconSectionRef, so focus moving
          // into it (search input, category buttons) reads as "outside". Keep the input section
          // mounted while a picker is open so interacting with it doesn't unmount and close it.
          if (document.querySelector('.EmojiPickerReact')) {
            return;
          }

          const isInputField = activeElement?.tagName === 'INPUT';
          const isTextArea = activeElement?.tagName === 'TEXTAREA';
          const isTipTapEditor = activeElement?.className === 'tiptap ProseMirror';
          const isContentEditable = activeElement?.getAttribute('contenteditable') === 'true';

          if (!isInputField && !isTextArea && !isTipTapEditor && !isContentEditable) {
            setIsKeyboardVisible(false);
          }
        }, 50);
      };

      document.addEventListener('focus', handleFocus, true);
      document.addEventListener('blur', handleBlur, true);

      return () => {
        document.removeEventListener('focus', handleFocus, true);
        document.removeEventListener('blur', handleBlur, true);
      };
    }, []);

    useEffect(() => {
      if (inlineRef?.current) {
        inlineRef.current.messageInputFocus();
      }
    }, [chatGroup]);

    useEffect(() => {
      if (!subNavigationRef?.current) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.borderBoxSize[0]?.blockSize || entry.contentRect.height;
          setSubNavigationHeight(height);
        }
      });

      resizeObserver.observe(subNavigationRef.current);

      const initialHeight = subNavigationRef.current.offsetHeight;
      setSubNavigationHeight(initialHeight);

      return () => {
        resizeObserver.disconnect();
      };
    }, [selectFile]);

    const getReplyMessage = useCallback(
      (reply: MessageReplyInterface | undefined) => {
        let replyMsg = displayedMessages?.find((message) => message.id === reply?.id)?.message;
        if (!replyMsg) {
          replyMsg = messages?.find((message) => message.id === reply?.id)?.message;
        }
        return replyMsg || reply?.message;
      },
      [displayedMessages, messages]
    );

    useEffect(() => {
      setReplyMessage({});
    }, [currentProject?.id, isNavbarMessage, params?.id]);

    useEffect(() => {
      const dropdown = document.querySelector('.dropdown-content');
      if (dropdown instanceof HTMLElement) {
        const rect = dropdown.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        if (rect.bottom + 10 > windowHeight) {
          dropdown.style.top = `-${rect.height + 5}px`;
        } else {
          dropdown.style.top = 'auto';
        }
      }
    }, [openEmoji]);

    const { onSendComment } = useSendMessage({
      content,
      editingMessage,
      replyMessage,
      currentProject,
      isNavbarMessage,
      currentUser,
      workspace,
      setDisplayedMessages,
      setReplyMessage,
      setEditingMessage,
      setAttachment,
      clearDraft,
      inlineRef,
      Cancel,
      rootStore
    });

    const onReactOnMessage = useCallback(
      async (emojiData: { emoji: string; imageUrl?: string }, item: MessageDetailsInterface) => {
        setEmojiLoading(true);
        trackAnalyticActivity(MESSAGE_ANALYTICS.MESSAGE_REACTED);
        closeEmojiDropdown();
        const updatedEmojis = [
          emojiData?.emoji,
          ...(Array.isArray(userUseEmoji) ? userUseEmoji.filter((emoji) => emoji !== emojiData?.emoji) : [])
        ]?.slice(0, 2);
        UserPreferenceSingleton.getInstance().setUserUseEmoji(updatedEmojis);

        const reactionUniqId = nanoid();

        const updateMessageReactions = (message: MessageDetailsInterface) => {
          if (message?.id !== item.id) return message;

          const existingReactions = message.reactions || [];
          const reactionsWithoutUser = existingReactions.filter((reaction) => reaction.UserId !== currentUser?.id);
          const hasReactedWithSameEmoji = existingReactions.some(
            (reaction) => reaction.ReactionType === emojiData.emoji && reaction.UserId === currentUser?.id
          );

          const updatedReactions = hasReactedWithSameEmoji
            ? reactionsWithoutUser
            : [
                ...reactionsWithoutUser,
                {
                  _id: reactionUniqId,
                  ReactionType: emojiData.emoji,
                  UserId: currentUser?.id!
                }
              ];

          return {
            ...message,
            reactions: updatedReactions
          };
        };

        setDisplayedMessages((prevMessages) => prevMessages.map(updateMessageReactions));

        if ((currentProject as ChatGroupInterface)?.messages && currentProject?.id) {
          rootStore.chatMessageStore.updateCurrentChatGroupMessages(currentProject.id, (current) =>
            current.map(updateMessageReactions)
          );
        }

        const payload = {
          reaction: emojiData?.emoji,
          commentId: item?.id,
          userId: currentUser?.id,
          companyId: workspace?.id,
          groupName: currentProject?.name
        };
        const response = await addReactionOnMessage(payload);
        if (response) {
          if (currentProject?.id) {
            syncConversation(
              currentProject.id,
              isNavbarMessage
                ? (currentProject as ChatGroupInterface)?.type ?? CHAT_GROUP_TYPE.PERSONAL
                : CHAT_GROUP_TYPE.PROJECT
            );
          }
          setEmojiLoading(false);
        }
      },
      [
        closeEmojiDropdown,
        currentProject,
        isNavbarMessage,
        currentUser?.id,
        setEmojiLoading,
        userUseEmoji,
        workspace?.id
      ]
    );

    const onClickReply = useCallback(
      (item: MessageDetailsInterface) => {
        setReplyMessage({
          id: item?.id,
          Message: item?.message,
          Files: item?.uploadedFile,
          CreatedTime: item?.createdOn,
          Sender: item?.sender?.name,
          Sender_id: item?.sender?.id
        });
      },
      [setReplyMessage]
    );

    const openDeleteMessageModal = useCallback(
      (itemId: string) => {
        setDeletedMessageId(itemId);
        setSelectedMessage(itemId);
      },
      [setSelectedMessage]
    );

    const closeDeleteMessageModal = useCallback(() => {
      setDeletedMessageId(null);
      setSelectedMessage(null);
    }, [setSelectedMessage]);

    const deleteMessage = useCallback(async () => {
      try {
        setDeleteLoading(true);
        if (!deletedMessageId) {
          rootStore.alertNotificationStore.setErrorMessage('Invalid message id! Try again later.');
          return;
        }
        const messageToDelete = displayedMessages.find((message) => message?.id === deletedMessageId);
        const payload = {
          IsArchived: true
        };
        const result = await updateDiscussionMessageDetail(deletedMessageId, payload);
        if (result) {
          // Delete only the files uploaded to this message from Firebase, keep the selected project files.
          messageToDelete?.uploadedFile?.forEach((file) => {
            if (file && !file.uploadFileType && file.href?.startsWith('https://firebasestorage.googleapis'))
              removeFileFromFirebase(file.href);
          });
          const updatedMessages = displayedMessages.filter((message) => message?.id !== deletedMessageId);
          setDisplayedMessages(updatedMessages);
          // Remove it from the store too (remembered/cached copies are built from it), then sync the
          // sidebar row and cache like send and react do.
          if (isNavbarMessage) {
            const chatStore = rootStore.chatMessageStore;
            const group = chatStore.currentChatGroup;
            if (group && group.id === currentProject?.id) {
              chatStore.setCurrentChatGroupMessages(
                group.id,
                (group.messages || []).filter((message) => message?.id !== deletedMessageId),
                {
                  cursor: chatStore.openChatCursor,
                  hasMore: chatStore.openChatHasMore,
                  updatedAt: chatStore.openChatUpdatedAt
                }
              );
            }
          } else {
            const { projectStore } = rootStore;
            projectStore.setDiscussionData(
              projectStore.discussionData.filter((message) => message?.id !== deletedMessageId)
            );
          }
          if (currentProject?.id) {
            syncConversation(
              currentProject.id,
              isNavbarMessage
                ? (currentProject as ChatGroupInterface)?.type ?? CHAT_GROUP_TYPE.PERSONAL
                : CHAT_GROUP_TYPE.PROJECT
            );
          }
          trackAnalyticActivity(MESSAGE_ANALYTICS.REMOVED);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setDeleteLoading(false);
        closeDeleteMessageModal();
      }
    }, [deletedMessageId, displayedMessages, closeDeleteMessageModal, currentProject, isNavbarMessage]);

    const updateMessageInState = useCallback((updatedHtmlString: string, messageId: string) => {
      setDisplayedMessages((prevMessages) =>
        prevMessages.map((message) =>
          message?.id === messageId ? { ...message, message: updatedHtmlString } : message
        )
      );
    }, []);

    const { figmaRefreshLoading, onClickHandleRefreshFigma } = useFigmaRefresh({
      updateMessageInState
    });

    const getCommentBody = useCallback((commentHtml: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(commentHtml, 'text/html');
      return doc.querySelector('body');
    }, []);

    const onClickCheckBox = useCallback(
      async (
        body: HTMLBodyElement | null,
        htmlElement: HTMLElement | null | undefined,
        item: MessageDetailsInterface
      ) => {
        if (!htmlElement || !body) return;
        const htmlElementDiv = htmlElement.querySelector('div');
        if (!htmlElementDiv) return;

        const htmlElementDivHTML = htmlElementDiv.innerHTML.trim();

        const matchingLi = Array.from(body.querySelectorAll('li[data-type="taskItem"]')).find((li) => {
          const liDiv = li.querySelector('div');
          return liDiv && liDiv.innerHTML.trim() === htmlElementDivHTML;
        });

        if (matchingLi) {
          const input = matchingLi.querySelector('input[type="checkbox"]');
          if (input) {
            if (input.hasAttribute('checked')) {
              matchingLi.setAttribute('data-checked', 'false');
              input.removeAttribute('checked');
            } else {
              matchingLi.setAttribute('data-checked', 'true');
              input.setAttribute('checked', 'true');
            }
          }
        }
        const updatedHtmlString = body.innerHTML;
        updateMessageInState(updatedHtmlString, item.id);
        await updateDiscussionMessageDetail(item?.id, { Message: updatedHtmlString });
      },
      [updateMessageInState]
    );

    const onClickHandleRichtextClick = useCallback(
      async (event: React.MouseEvent, item: MessageDetailsInterface) => {
        try {
          const clickedElement = event.target as HTMLElement;
          const body = getCommentBody(item?.message || '');
          if (!body) return;
          const subParentElement = clickedElement.parentElement;
          const parentElement = subParentElement?.parentElement;
          if (
            parentElement &&
            parentElement.tagName.toLowerCase() === 'li' &&
            parentElement.getAttribute('data-type') === 'taskItem'
          ) {
            onClickCheckBox(body, parentElement, item);
          }
        } catch (e) {
          captureException(e);
        }
      },
      [getCommentBody, onClickCheckBox]
    );

    const attachmentPage = useCallback(
      (isCommentModalParam: boolean) => {
        setIsCommentModal(isCommentModalParam);
        setAttachment(true);
        if (projectAllFiles && projectAllFiles.length > 0) {
          rootStore.projectStore.currentProject?.setProjectFiles(projectAllFiles);
        }
        if (mobile) {
          setSelectFile(true);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isCommentModal, mobile, projectAllFiles, setAttachment, setSelectFile]
    );

    const handleOpenSnapModal = useCallback(() => {
      setIsSnapModalOpen(true);
    }, []);

    const handleCloseSnapModal = useCallback(() => {
      setIsSnapModalOpen(false);
    }, []);

    const handleOpenGifModal = useCallback(() => {
      setIsOpenGifModal(true);
    }, []);

    const handleCloseGifModal = useCallback(() => {
      setIsOpenGifModal(false);
    }, []);

    const onClickUserPopup = useCallback((member: UserModel) => {
      rootStore.appStore.setUserModalDetail(member);
      setIsOpenUserModal(true);
    }, []);

    const onCloseUserModal = useCallback(() => {
      setIsOpenUserModal(false);
    }, []);

    // ---------------------------------------------------------------------------
    // Three focused context values
    // ---------------------------------------------------------------------------

    /**
     * messageActionsValue — stable callbacks only.
     * Dependencies are all stable callbacks/refs, so this object almost never
     * changes, preventing cascading re-renders in consumers.
     */
    const messageActionsValue: MessageActionsContextValue = useMemo(
      () => ({
        onReactOnMessage,
        onClickReply,
        editMessageData,
        openDeleteMessageModal,
        handleOpenChange,
        setSelectedMessage,
        setOpenEmoji,
        onClickHandleRichtextClick,
        onClickHandleRefreshFigma,
        onClickFile,
        getReplyMessage,
        onClickUserPopup
      }),
      [
        onReactOnMessage,
        onClickReply,
        editMessageData,
        openDeleteMessageModal,
        handleOpenChange,
        setSelectedMessage,
        setOpenEmoji,
        onClickHandleRichtextClick,
        onClickHandleRefreshFigma,
        onClickFile,
        getReplyMessage,
        onClickUserPopup
      ]
    );

    /**
     * messageSelectionValue — volatile selection/UI state.
     * Only components that truly need selection state will re-render when
     * openEmoji, selection, etc. change.
     */
    const messageSelectionValue: MessageSelectionContextValue = useMemo(
      () => ({
        selectedMesaage,
        openEmoji,
        deletedMessageId,
        emojiLoading,
        figmaRefreshLoading,
        emojiButtonRef
      }),
      [selectedMesaage, openEmoji, deletedMessageId, emojiLoading, figmaRefreshLoading, emojiButtonRef]
    );

    /**
     * messageEnvironmentValue — stable environment / user / workspace info.
     * Changes only when the user, workspace, or layout context changes.
     */
    const messageEnvironmentValue: MessageEnvironmentContextValue = useMemo(
      () => ({
        mobile,
        isMessage: environment.isMessage,
        userUseEmoji,
        workspace,
        currentUser,
        isProjectMessageDetail: environment.isProjectMessageDetail
      }),
      [mobile, environment.isMessage, userUseEmoji, workspace, currentUser, environment.isProjectMessageDetail]
    );

    const messageInput = (
      <MessageInputSection
        inlineRef={inlineRef}
        onClickSelectFile={() => attachmentPage(false)}
        isNavbarMessage={isNavbarMessage}
        currentProject={currentProject}
        sendComment={() => onSendComment(uploadedFiles)}
        replyMessage={replyMessage}
        setReplyMessage={() => setReplyMessage({})}
        isProjectDetail={isProjectDetail}
        handleOpenSnapModal={handleOpenSnapModal}
        handleOpenGifModal={handleOpenGifModal}
        isAutoFocus={!newGroup}
        disable={isOpenEditMessageModal}
        isKeyboardVisible={isKeyboardVisible}
        iconSectionRef={iconSectionRef}
      />
    );

    return (
      <MessageFeedProvider
        environment={environment}
        fileSelection={fileSelectionState}
        messageActions={messageActionsValue}
        messageSelection={messageSelectionValue}
        messageEnvironment={messageEnvironmentValue}>
        <Group
          isProjectMessageDetail={isProjectMessageDetail && !selectFile}
          className={isProjectMessageDetail ? 'project-message-detail' : ''}
          newGroup={newGroup}
          selectFileResponsive={selectFile}>
          {selectFile ? (
            <RenderExistingFileV2
              setAttachment={() => setAttachment(false)}
              setSelectFile={(value: boolean) => setSelectFile(value)}
              onSelectFile={onSelectFile}
            />
          ) : (
            <>
              <MessageList
                messages={messages}
                loading={loading}
                displayedMessages={displayedMessages}
                setDisplayedMessages={setDisplayedMessages}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                isMessageLoading={isMessageLoading}
                newGroupState={newGroupState}
                containerRef={containerRef}
                isBubbleMenuOpen={isBubbleMenuOpen}
                subNavigationHeight={subNavigationHeight}
                canLoadOlder={
                  isNavbarMessage ? rootStore.chatMessageStore.openChatHasMore : rootStore.projectStore.discussionHasMore
                }
                isLoadingOlder={
                  isNavbarMessage
                    ? rootStore.chatMessageStore.openChatLoadingOlder
                    : rootStore.projectStore.discussionLoadingOlder
                }
                loadOlderFailures={
                  isNavbarMessage
                    ? rootStore.chatMessageStore.openChatLoadOlderFailures
                    : rootStore.projectStore.discussionLoadOlderFailures
                }>
                {!mobile && !newGroup && !(loading && (messages?.length === 0 || !messages)) && (
                  <Footer>{messageInput}</Footer>
                )}
              </MessageList>
              <MessageFileDrawer onSelectFile={onSelectFile} isCommentModal={isCommentModal} />
              <MessageModals
                isSnapModalOpen={isSnapModalOpen}
                handleCloseSnapModal={handleCloseSnapModal}
                deletedMessageId={deletedMessageId}
                closeDeleteMessageModal={closeDeleteMessageModal}
                deleteMessage={deleteMessage}
                deleteLoading={deleteLoading}
                isOpenEditMessageModal={isOpenEditMessageModal}
                Cancel={Cancel}
                inlineRef={inlineRef}
                onSendComment={onSendComment}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={(
                  files: CommentFilesInterface[] | ((prevFiles: CommentFilesInterface[]) => CommentFilesInterface[])
                ) => {
                  if (typeof files === 'function') {
                    rootStore.richTextStore.setUploadedFiles(files(rootStore.richTextStore.uploadedFiles));
                    return;
                  }
                  rootStore.richTextStore.setUploadedFiles(files);
                }}
                content={content}
                setValueContent={(value: string) => rootStore.richTextStore.setContent(value)}
                loading={loading}
                setPropsLoading={(value: boolean) => rootStore.richTextStore.setLoading(value)}
                currentProject={currentProject}
                isNavbarMessage={isNavbarMessage}
                attachmentPage={attachmentPage}
                handleOpenSnapModal={handleOpenSnapModal}
                editingMessage={!!editingMessage}
                openGifModal={openGifModal}
                handleCloseGifModal={handleCloseGifModal}
                isOpenUserModal={isOpenUserModal}
                onCloseUserModal={onCloseUserModal}
              />
              <SubNavigation
                ref={subNavigationRef}
                isMessage
                centerElement={mobile && !newGroup && messageInput}
                rightElement={rightElement}
                isKeyboardVisible={isKeyboardVisible}
              />
            </>
          )}
        </Group>
      </MessageFeedProvider>
    );
  }
);

export default MessagesFeed;
